<template>
  <v-container fluid class="pa-6 fade-in">
    <!-- PR-FREE-WINS-CLARITY: Small Wins capture is FREE for everyone, so this
         page no longer mounts a premium prompt. Only the optional "Capture with
         Sage" actions are gated, and those are hidden for free users via
         v-if="isPremium || companyViewEnabled". The one shared
         UpgradeToPlusModal (App.vue) covers any Plus prompt app-wide. -->
    <v-overlay color="primary" :absolute="true" :value="loading">
      <v-progress-circular indeterminate color="accent" size="64" />
    </v-overlay>

    <!-- RESPONSIVE-HEADER-FIX: stack the title/description above the action
         buttons on small screens and let the buttons wrap instead of
         overflowing off the right edge. On desktop the title block flexes to
         fill the available space and the buttons sit on the right. -->
    <v-row>
      <v-col cols="12">
        <div
          class="d-flex flex-column flex-md-row justify-space-between align-start"
        >
          <div class="header-text-block">
            <div class="headline-text">Small Wins & Projects</div>
            <div class="text-body-2 text--secondary">
              Log small wins, learnings, events, and portfolio pieces.
            </div>
            <!-- PR-HUB-COACH-POLISH: clarify the win vs accomplishment
                 distinction right where wins are logged. -->
            <div class="text-body-2 text--secondary mt-1">
              Wins live on your profile, tag them, add STAR detail, assign them
              to a job. Accomplishments live on your resume.
            </div>
          </div>
          <div
            class="d-flex flex-wrap header-actions mt-3 mt-md-0"
            :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : ''"
          >
            <!-- PR-HUB-COACH-POLISH: jump to the Period Summary report from
                 where wins are logged. -->
            <v-btn
              outlined
              color="primary"
              class="mr-2 mb-2"
              :block="$vuetify.breakpoint.xsOnly"
              @click="$router.push('/SmallWinsSummary')"
            >
              <v-icon left>mdi-chart-box-outline</v-icon>
              Period Summary
            </v-btn>
            <v-btn
              v-if="isPremium || companyViewEnabled"
              color="primary"
              class="mr-2 mb-2"
              :block="$vuetify.breakpoint.xsOnly"
              @click="goToSage"
            >
              <v-icon left>mdi-message-text</v-icon>
              Capture with Sage
            </v-btn>
            <v-btn
              outlined
              color="primary"
              class="mb-2"
              :block="$vuetify.breakpoint.xsOnly"
              @click="showAddForm = !showAddForm"
            >
              <v-icon left>mdi-plus</v-icon>
              Add Manually
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-expand-transition>
      <v-row v-if="showAddForm">
        <v-col cols="12">
          <v-card outlined class="r-card pa-4 mb-2">
            <div class="text-subtitle-1 font-weight-medium mb-1">Add New Entry</div>
            <div class="text-body-2 text--secondary mb-3">Quick capture a small win, project, or learning moment</div>
            <v-row align="center">
              <v-col cols="12" md="9">
                <v-text-field
                  v-model="newEntryDescription"
                  outlined
                  dense
                  hide-details
                  placeholder="e.g. Closed the Acme deal, finished Q3 report ahead of schedule..."
                  @keyup.enter="addNewEntry"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="3">
                <v-btn
                  color="primary"
                  block
                  :disabled="!newEntryDescription || !newEntryDescription.trim()"
                  :loading="addingEntry"
                  @click="addNewEntry"
                >
                  <v-icon left>mdi-content-save</v-icon>
                  Add Entry
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-expand-transition>

    <v-row>
      <v-col cols="12">
        <v-card flat class="simpleCard">
          <v-card-title class="d-flex justify-space-between">
            <span class="section-title">All Entries ({{ filteredItems.length }})</span>
          </v-card-title>
          <v-card-text>
            <v-row align="center" class="mb-4">
              <v-col cols="4" md="2" class="pr-2">
                <v-select
                  v-model="filters.status"
                  :items="statusOptions"
                  outlined
                  dense
                  hide-details
                  clearable
                  placeholder="Status"
                />
              </v-col>

              <v-col cols="4" md="2" class="pr-2">
                <v-select
                  v-model="filters.type"
                  :items="typeOptions"
                  outlined
                  dense
                  hide-details
                  clearable
                  placeholder="Type"
                />
              </v-col>

              <v-col cols="8" md="6" class="pr-2">
                <v-text-field
                  v-model="filters.search"
                  outlined
                  dense
                  hide-details
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Search"
                  clearable
                />
              </v-col>

              <v-col cols="4" md="2" class="d-flex justify-end">
                <v-btn text @click="resetFilters">Reset</v-btn>
              </v-col>
            </v-row>
          </v-card-text>
          <!-- MOBILE-TABLE-FIX: raise the mobile-breakpoint so the table
               stacks each row into a label/value card on phones AND tablets
               (default 600 left the action + summary columns squished on
               narrow screens). Desktop (>=960) keeps the column layout. -->
          <v-data-table
            :headers="headers"
            :items="filteredItems"
            class="elevation-0 small-wins-table"
            :items-per-page="10"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :mobile-breakpoint="960"
            :footer-props="{ 'items-per-page-options': [5, 10, 25, 50] }"
          >
            <template v-slot:item.summary="{ item }">
              <div class="py-3 description-cell">
                {{ getItemSummary(item) }}
                <!-- PR-WIN-GRADUATE-CONFIRM: unmistakable marker once a win has
                     been promoted into a Work Accomplishment on the resume.
                     Solid green chip with a check so it reads at a glance. -->
                <v-chip
                  v-if="item.graduated"
                  class="ml-1 mt-1 on-resume-chip"
                  x-small
                  color="success"
                  dark
                >
                  <v-icon x-small left>mdi-check-circle</v-icon>
                  On resume
                </v-chip>
              </div>
            </template>

            <template v-slot:item.status="{ item }">
              <v-chip
                :class="item.status === 'verified' ? 'chip-verified' : 'chip-unverified'"
                small
                @click="toggleVerify(item)"
                style="cursor: pointer;"
              >
                <v-icon x-small left>{{ getStatusIcon(item.status) }}</v-icon>
                {{ getStatusLabel(item.status) }}
              </v-chip>
            </template>

            <template v-slot:item.parsedType="{ item }">
              <v-chip
                :color="getTypeColor(item.parsedType)"
                small
                outlined
              >
                {{ getTypeLabel(item.parsedType) }}
              </v-chip>
            </template>

            <template v-slot:item.linkedTo="{ item }">
              <v-chip
                v-if="item.linkedTo && getLinkedJobLabel(item.linkedTo)"
                color="teal lighten-4"
                text-color="teal darken-3"
                x-small
              >
                <v-icon x-small left>mdi-link</v-icon>
                {{ getLinkedJobLabel(item.linkedTo) }}
              </v-chip>
              <span v-else class="grey--text text--lighten-1">--</span>
            </template>

            <template v-slot:item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>

            <template v-slot:item.actions="{ item }">
              <v-menu offset-y left>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small v-bind="attrs" v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item @click="openEditDialog(item)">
                    <v-list-item-icon><v-icon>mdi-pencil</v-icon></v-list-item-icon>
                    <v-list-item-title>Edit</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="isPremium || companyViewEnabled" @click="expandToSage(item)">
                    <v-list-item-icon><v-icon>mdi-message-text</v-icon></v-list-item-icon>
                    <v-list-item-title>Go Deeper with Sage</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'inferred'" @click="verifyItem(item)">
                    <v-list-item-icon><v-icon color="success">mdi-check-circle</v-icon></v-list-item-icon>
                    <v-list-item-title>Mark as Verified</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="item.status === 'verified'" @click="unverifyItem(item)">
                    <v-list-item-icon><v-icon color="warning">mdi-undo</v-icon></v-list-item-icon>
                    <v-list-item-title>Mark as Needs Review</v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="!item.graduated" @click="openGradDialog(item)">
                    <v-list-item-icon><v-icon color="primary">mdi-file-account-outline</v-icon></v-list-item-icon>
                    <v-list-item-title>Add to my resume</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="confirmDelete(item)">
                    <v-list-item-icon><v-icon color="error">mdi-delete</v-icon></v-list-item-icon>
                    <v-list-item-title class="error--text">Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>

            <template v-slot:no-data>
              <div class="empty-state">
                <v-icon class="empty-state-icon">mdi-trophy-outline</v-icon>
                <div class="empty-state-title">No wins captured yet</div>
                <div class="empty-state-subtitle">Start by logging a quick win or talk to Sage to capture your accomplishments.</div>
                <div class="d-flex justify-center" style="gap: 12px;">
                  <v-btn v-if="isPremium || companyViewEnabled" color="primary" @click="goToSage">
                    <v-icon left>mdi-message-text</v-icon>
                    Capture with Sage
                  </v-btn>
                  <v-btn color="primary" outlined @click="showAddForm = !showAddForm">
                    <v-icon left>mdi-plus</v-icon>
                    Add Manually
                  </v-btn>
                </div>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog v-model="editDialog" max-width="700" scrollable persistent>
      <v-card class="r-card">
        <v-card-title class="d-flex align-start justify-space-between pa-6">
          <div>
            <div class="dialog-title font-weight-medium">Edit Entry</div>
            <div class="text-body-2 grey--text mt-1">
              Update the details and STAR breakdown for this capture.
            </div>
          </div>
          <v-btn icon small @click="closeEditDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <v-form ref="editForm" v-model="formValid">
            <div class="text-subtitle-2 font-weight-medium mb-1">
              What happened? <span class="error--text">*</span>
            </div>
            <v-textarea
              v-model="editItem.rawInput"
              :rules="[rules.required]"
              :maxlength="2000"
              outlined
              dense
              auto-grow
              rows="3"
              hide-details="auto"
            />

            <v-divider class="my-5"></v-divider>

            <div class="text-subtitle-2 font-weight-bold mb-3">STAR Details</div>

            <div class="text-subtitle-2 font-weight-medium mb-1">
              Situation / Context
            </div>
            <v-textarea
              v-model="editItem.situation"
              outlined
              dense
              auto-grow
              rows="2"
              hide-details
              placeholder="e.g. Our team was behind schedule on a critical product launch with only 3 weeks left..."
            />

            <div class="text-subtitle-2 font-weight-medium mt-4 mb-1">
              Task / Challenge
            </div>
            <v-textarea
              v-model="editItem.task"
              outlined
              dense
              auto-grow
              rows="2"
              hide-details
              placeholder="e.g. I was responsible for coordinating the remaining deliverables across 3 teams..."
            />

            <div class="text-subtitle-2 font-weight-medium mt-4 mb-1">
              Action You Took
            </div>
            <v-textarea
              v-model="editItem.action"
              outlined
              dense
              auto-grow
              rows="2"
              hide-details
              placeholder="e.g. I created a daily standup cadence, reprioritized the backlog, and personally unblocked two critical dependencies..."
            />

            <div class="text-subtitle-2 font-weight-medium mt-4 mb-1">
              Result / Impact
            </div>
            <v-textarea
              v-model="editItem.result"
              outlined
              dense
              auto-grow
              rows="2"
              hide-details
              placeholder="e.g. We shipped on time, reduced scope creep by 40%, and the feature drove $200K in new revenue within Q1..."
            />

            <v-divider class="my-5"></v-divider>

            <div class="text-subtitle-2 font-weight-medium mb-1">
              <v-icon small class="mr-1">mdi-calendar</v-icon>
              Date (month/year)
            </div>
            <v-row>
              <v-col cols="6">
                <v-select
                  v-model="editItem.eventMonth"
                  :items="monthOptions"
                  outlined
                  dense
                  hide-details
                  label="Month"
                ></v-select>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model.number="editItem.eventYear"
                  outlined
                  dense
                  hide-details
                  label="Year"
                  type="number"
                  :min="2000"
                  :max="2099"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-divider class="my-5"></v-divider>

            <div v-if="hasJobs" class="mb-4">
              <div class="text-subtitle-2 font-weight-medium mb-2">
                <v-icon small class="mr-1">mdi-link-variant</v-icon>
                Link to a role (optional)
              </div>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="job in jobOptions"
                  :key="job.key"
                  :color="editItem.linkedTo === job.key ? 'primary' : 'grey lighten-3'"
                  :text-color="editItem.linkedTo === job.key ? 'white' : 'grey darken-2'"
                  small
                  class="mr-2 mb-2"
                  @click="toggleLinkedJob(job.key)"
                >
                  {{ job.label }}
                  <v-icon v-if="editItem.linkedTo === job.key" x-small right>mdi-check</v-icon>
                </v-chip>
              </div>
            </div>

            <div class="text-subtitle-2 font-weight-medium mb-2">Tags (optional)</div>
            <v-row no-gutters align="center">
              <v-col cols="12" md="9" class="pr-md-3">
                <v-text-field
                  v-model="newTag"
                  outlined
                  dense
                  hide-details
                  placeholder="Add a new tag..."
                  @keyup.enter="addTag"
                />
              </v-col>
              <v-col cols="12" md="3" class="mt-2 mt-md-0 d-flex justify-end">
                <v-btn
                  color="primary"
                  outlined
                  small
                  @click="addTag"
                  :disabled="!newTag.trim()"
                >
                  <v-icon small class="mr-1">mdi-plus</v-icon>
                  Add
                </v-btn>
              </v-col>
            </v-row>
            <div class="d-flex flex-wrap mt-3">
              <v-chip
                v-for="tag in combinedTags"
                :key="tag"
                class="mr-2 mb-2"
                small
                :outlined="!isTagSelected(tag)"
                :color="isTagSelected(tag) ? 'primary' : undefined"
                :text-color="isTagSelected(tag) ? 'white' : undefined"
                @click="toggleTag(tag)"
              >
                {{ isTagSelected(tag) ? tag : '+ ' + tag }}
              </v-chip>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <!-- PR-WIN-GRADUATE-CONFIRM: once this win is on the resume show a
               clear green marker instead of the promote action; it cannot be
               re-promoted. The button now OPENS the two-phase confirm dialog. -->
          <v-chip
            v-if="editItemWin && editItemWin.graduated"
            class="on-resume-chip"
            small
            color="success"
            dark
          >
            <v-icon x-small left>mdi-check-circle</v-icon>
            On resume
          </v-chip>
          <v-btn
            v-else
            text
            color="primary"
            @click="openGradDialogFromEdit"
            :loading="graduating"
          >
            <v-icon class="mr-1">mdi-file-account-outline</v-icon>
            Add to my resume
          </v-btn>
          <v-spacer />
          <v-btn text @click="closeEditDialog">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveEntry"
            :disabled="!formValid"
            :loading="saving"
          >
            <v-icon class="mr-1">mdi-content-save-outline</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- PR-WIN-GRADUATE-CONFIRM: two-phase "Add to my resume" dialog.
         Phase 1 (confirm) explains what graduation does and requires a role
         pick; phase 2 (success) confirms the result with a route into the
         Work Accomplishments editor. The same dialog flips between phases so
         the user sees a clear before -> after, not just a fleeting snackbar. -->
    <v-dialog v-model="gradDialog" max-width="560" persistent scrollable>
      <v-card class="r-card">
        <!-- Phase 1: confirm -->
        <template v-if="gradPhase === 'confirm'">
          <v-card-title class="d-flex align-start justify-space-between pa-6">
            <div>
              <div class="dialog-title font-weight-medium">Add to my resume</div>
              <div class="text-body-2 grey--text mt-1">
                Copy this win into your Work Experience.
              </div>
            </div>
            <v-btn icon small @click="closeGradDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pt-2">
            <div class="text-body-2 mb-4">
              Adding to your resume copies this win's STAR story into your Work
              Experience under a role, so it shows up on your resume and in
              resume generation. It stays here in Small Wins, marked "On
              resume."
            </div>

            <div class="text-subtitle-2 font-weight-medium mb-1">
              Add under which role? <span class="error--text">*</span>
            </div>
            <v-select
              v-model="gradRole"
              :items="jobOptions"
              item-text="label"
              item-value="key"
              outlined
              dense
              hide-details="auto"
              label="Select a role"
              persistent-hint
              hint="You can change which role this is tagged to."
            ></v-select>

            <!-- PR-WIN-GRADUATE-OPTIMIZE: refine the bullet BEFORE it lands on
                 the resume. The textarea is editable for everyone (manual
                 edit); the ai-optimize-phrasing control lets Plus users
                 optimize with AI and routes free users to the upgrade modal
                 when their attempts run out. Whatever text is here on confirm
                 is what the accomplishment shows on the resume. -->
            <div class="mt-4">
              <div class="text-subtitle-2 font-weight-medium mb-1">
                What will be added
              </div>
              <div class="text-caption grey--text text--darken-1 mb-2">
                Edit this bullet, or optimize it with AI, before adding it.
              </div>
              <v-textarea
                v-model="gradBulletText"
                outlined
                dense
                auto-grow
                rows="3"
                hide-details
                placeholder="The accomplishment bullet that will appear on your resume."
              ></v-textarea>
              <ai-optimize-phrasing
                :phrasing="gradBulletText"
                context="work-accomplishment"
                defaultLength="medium"
                @onAcceptPhrasing="onGradPhrasingAccepted"
              ></ai-optimize-phrasing>
            </div>

            <v-alert
              v-if="gradError"
              type="error"
              dense
              text
              class="mt-4 mb-0"
            >
              {{ gradError }}
            </v-alert>
          </v-card-text>

          <v-card-actions class="px-6 pb-4">
            <v-spacer />
            <v-btn text @click="closeGradDialog">Cancel</v-btn>
            <v-btn
              color="primary"
              :disabled="!gradRole"
              :loading="graduating"
              @click="confirmGraduate"
            >
              <v-icon class="mr-1">mdi-file-account-outline</v-icon>
              Add to resume
            </v-btn>
          </v-card-actions>
        </template>

        <!-- Phase 2: success -->
        <template v-else>
          <v-card-text class="text-center pa-8">
            <v-icon color="success" size="64" class="mb-3">
              mdi-check-circle
            </v-icon>
            <div class="dialog-title font-weight-medium mb-2">
              Added to your resume under {{ gradResultLabel }}
            </div>
            <div class="text-body-2 grey--text">
              It's in your Work Experience now and will flow into your resumes.
            </div>
          </v-card-text>
          <v-card-actions class="px-6 pb-6 d-flex justify-center">
            <v-btn outlined color="primary" @click="viewInExperience">
              <v-icon class="mr-1">mdi-briefcase-outline</v-icon>
              View in Experience
            </v-btn>
            <v-btn color="primary" @click="closeGradDialog">Done</v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card>
        <v-card-title class="pa-6">
          <div class="dialog-title font-weight-medium">Confirm Delete</div>
        </v-card-title>
        <v-card-text class="px-6">
          <!-- PR-WIN-GRADUATE-INTEGRITY: deleting a graduated win does not
               touch the resume accomplishment, so warn the user first. -->
          <v-alert
            v-if="deleteGraduatedWarning"
            type="warning"
            dense
            text
            class="mb-3"
          >
            {{ deleteGraduatedWarning }}
          </v-alert>
          Are you sure you want to delete this entry? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pb-4 px-6">
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn
            color="error"
            outlined
            @click="deleteEntry"
            :loading="saving"
          >
            <v-icon class="mr-1">mdi-delete-outline</v-icon>
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarText }}
    </v-snackbar>
  </v-container>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { mapFields } from "vuex-map-fields";
import * as SmallWinsApi from "../../api/SmallWinsApi";
import * as ProfileContextApi from "../../api/ProfileContextApi";
import * as uuid from "../../utility/guid";
// PR-WIN-GRADUATE-OPTIMIZE: reuse the same AI-optimize component the Work
// Accomplishments page uses so Plus users can AI-optimize the bullet and free
// users edit it manually before it lands on the resume. The component handles
// the Plus gating + usage-limit / upgrade-modal internally.
import AiOptimizePhrasing from "../../components/AiOptimizePhrasing";
export default {
  name: "SmallWinsProjects",
  components: {
    "ai-optimize-phrasing": AiOptimizePhrasing,
  },
  data: function() {
    return {
      loading: false,
      saving: false,
      graduating: false,
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success",

      showAddForm: false,
      newEntryDescription: "",
      addingEntry: false,

      sortBy: "createdAt",
      sortDesc: true,

      filters: {
        status: null,
        type: null,
        search: "",
      },

      editDialog: false,
      deleteDialog: false,
      formValid: false,

      // PR-WIN-GRADUATE-CONFIRM: two-phase "Add to my resume" dialog state.
      // phase "confirm" shows the explanation + required role pick; phase
      // "success" shows the green confirmation with a "View in Experience"
      // link. gradWin is the win being promoted, gradRole the SELECTED role
      // key (defaults to the win's linkedTo but the user can move it), and
      // gradError holds an inline failure message so the user can retry.
      gradDialog: false,
      gradPhase: "confirm",
      gradWin: null,
      gradRole: null,
      gradResultKey: null,
      gradResultLabel: "",
      gradError: "",
      // PR-WIN-GRADUATE-OPTIMIZE: the editable bullet the accomplishment will
      // be saved with. Seeded from the win's joined STAR (displayAuto) when the
      // dialog opens. Plus users refine it via <ai-optimize-phrasing>; free
      // users edit the textarea directly. Whatever is here on confirm becomes
      // the achievement's displayCustom (with useCustom: true).
      gradBulletText: "",

      newTag: "",

      editItem: {
        id: null,
        rawInput: "",
        situation: "",
        task: "",
        action: "",
        result: "",
        linkedTo: null,
        tags: [],
        eventDate: null,
      },

      deleteItemId: null,
      deleteItemHint: null,
      // PR-WIN-GRADUATE-INTEGRITY: warning shown in the delete-confirm dialog
      // when the win being deleted is on the resume. Empty for non-graduated
      // wins.
      deleteGraduatedWarning: "",

      headers: [
        { text: "Summary", value: "summary", sortable: false, width: "32%" },
        { text: "Status", value: "status", sortable: true, width: "12%" },
        { text: "Type", value: "parsedType", sortable: true, width: "12%" },
        { text: "Linked Role", value: "linkedTo", sortable: false, width: "18%" },
        { text: "Date", value: "createdAt", sortable: true, width: "14%" },
        { text: "", value: "actions", sortable: false, width: "6%", align: "center" },
      ],

      statusOptions: [
        { text: "Needs Review", value: "inferred" },
        { text: "Verified", value: "verified" },
      ],

      typeOptions: [
        { text: "Achievement", value: "achievement" },
        { text: "Note", value: "note" },
        { text: "Skill", value: "skill_signal" },
      ],

      rules: {
        required: function(v) { return !!(v && String(v).trim()) || "This field is required"; },
      },
    };
  },

  computed: {
    ...mapFields("smallWins", {
      storeItems: "items",
      isLoaded: "isLoaded",
    }),
    ...mapFields("auth", { isPremium: "isPremium" }),
    ...mapState("smallWins", ["availableTags", "tagsLoaded"]),
    ...mapState("work", { workDict: "workDict" }),
    ...mapState("settings", ["companyViewEnabled"]),

    jobOptions: function() {
      var jobs = [];
      if (this.workDict) {
        for (var key in this.workDict) {
          var job = this.workDict[key];
          if (job && job.workGeneral) {
            jobs.push({
              key: key,
              label: this.getJobLabel(job),
              isCurrent: job.workGeneral.workingCurrently || false
            });
          }
        }
      }
      jobs.sort(function(a, b) { return (b.isCurrent ? 1 : 0) - (a.isCurrent ? 1 : 0); });
      return jobs;
    },

    hasJobs: function() {
      return this.jobOptions.length > 0;
    },

    monthOptions: function() {
      return [
        { text: "January", value: 1 },
        { text: "February", value: 2 },
        { text: "March", value: 3 },
        { text: "April", value: 4 },
        { text: "May", value: 5 },
        { text: "June", value: 6 },
        { text: "July", value: 7 },
        { text: "August", value: 8 },
        { text: "September", value: 9 },
        { text: "October", value: 10 },
        { text: "November", value: 11 },
        { text: "December", value: 12 },
      ];
    },

    combinedTags: function() {
      var backendTags = this.availableTags || [];
      var itemTags = this.editItem.tags || [];
      var combined = [];
      var seen = {};
      backendTags.concat(itemTags).forEach(function(t) {
        var lower = t.toLowerCase();
        if (!seen[lower]) {
          seen[lower] = true;
          combined.push(t);
        }
      });
      return combined.sort(function(a, b) {
        return a.toLowerCase().localeCompare(b.toLowerCase());
      });
    },

    // PR-WIN-GRADUATE: the full store win backing the edit dialog. The dialog
    // edits a flattened editItem copy, but promotion needs the real win
    // (aiDraft, linkedTo, graduated flag) so we resolve it by id.
    editItemWin: function() {
      if (!this.editItem || !this.editItem.id) return null;
      var items = this.storeItems || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i] && items[i].id === this.editItem.id) {
          return items[i];
        }
      }
      return null;
    },

    filteredItems: function() {
      var items = (this.storeItems || []).slice();
      var result = items;

      if (this.filters.status) {
        var statusFilter = this.filters.status;
        result = result.filter(function(i) { return i.status === statusFilter; });
      }

      if (this.filters.type) {
        var typeFilter = this.filters.type;
        result = result.filter(function(i) { return i.parsedType === typeFilter; });
      }

      var q = (this.filters.search || "").toLowerCase().trim();
      if (q) {
        result = result.filter(function(i) {
          var summary = ((i.aiDraft && i.aiDraft.summary) || i.rawInput || "").toLowerCase();
          return summary.includes(q);
        });
      }

      return result;
    },
  },

  mounted: function() {
    this.loadData();
  },

  methods: {
    ...mapActions("smallWins", [
      "loadSmallWins",
      "loadAvailableTags",
    ]),

    loadData: function() {
      var self = this;
      this.loading = true;
      Promise.all([this.loadSmallWins(), this.loadAvailableTags()])
        .finally(function() {
          self.loading = false;
          self.tryAutoOpenEdit();
        });
    },

    // PR-WIN-EDIT-PARITY: when the user clicks Edit on a recent capture
    // from the What's New? page, that page routes here with ?edit=<id>.
    // Auto-open the full edit dialog for that win, then strip the query
    // param so a refresh doesn't re-trigger it.
    tryAutoOpenEdit: function() {
      var targetId = this.$route.query.edit;
      if (!targetId) return;
      var items = this.storeItems || [];
      var found = null;
      for (var i = 0; i < items.length; i++) {
        if (items[i] && items[i].id === targetId) {
          found = items[i];
          break;
        }
      }
      if (found) {
        this.openEditDialog(found);
      }
      var clean = Object.assign({}, this.$route.query);
      delete clean.edit;
      this.$router
        .replace({ path: this.$route.path, query: clean })
        .catch(function() {});
    },

    resetFilters: function() {
      this.filters = { status: null, type: null, search: "" };
    },

    goToSage: function() {
      this.$router.push("/Sage");
    },

    goToCapture: function() {
      this.$router.push("/SmallWinsCapture");
    },

    addNewEntry: function() {
      if (!this.newEntryDescription || !this.newEntryDescription.trim()) return;
      var self = this;
      this.addingEntry = true;
      SmallWinsApi.CreateSmallWin(
        this.newEntryDescription.trim(),
        "note",
        null,
        "inferred",
        null
      ).then(function(result) {
        if (result.success) {
          self.showSnackbar("Entry added!", "success");
          self.newEntryDescription = "";
          self.showAddForm = false;
          ProfileContextApi.ClearProfileContextCache();
          self.$store.dispatch("smallWins/loadSmallWins");
          self.loadData();
        } else {
          self.showSnackbar("Failed to add entry: " + (result.msg || "Unknown error"), "error");
        }
      }).catch(function(e) {
        console.error("Add entry error:", e);
        self.showSnackbar("Error adding entry", "error");
      }).finally(function() {
        self.addingEntry = false;
      });
    },

    getItemSummary: function(item) {
      return (item.aiDraft && item.aiDraft.summary) || item.rawInput || item.description || "";
    },

    getStatusColor: function(status) {
      var colors = { inferred: "warning", reviewed: "info", verified: "success" };
      return colors[status] || "grey";
    },

    getStatusIcon: function(status) {
      var icons = { inferred: "mdi-alert-circle-outline", verified: "mdi-check-circle", reviewed: "mdi-eye" };
      return icons[status] || "mdi-circle-outline";
    },

    getStatusLabel: function(status) {
      var labels = { inferred: "Needs Review", reviewed: "Reviewed", verified: "Verified" };
      return labels[status] || status || "Unknown";
    },

    getTypeColor: function(type) {
      var colors = { achievement: "primary", skill_signal: "teal", note: "grey" };
      return colors[type] || "grey";
    },

    getTypeLabel: function(type) {
      var labels = { achievement: "Achievement", skill_signal: "Skill", note: "Note" };
      return labels[type] || type || "Note";
    },

    getJobLabel: function(job) {
      var title = (job.workRole && job.workRole.positions && job.workRole.positions[0] && job.workRole.positions[0].title) || (job.workGeneral && job.workGeneral.display) || "Role";
      var company = (job.workGeneral && job.workGeneral.companyName) || "";
      return company ? title + " @ " + company : title;
    },

    getLinkedJobLabel: function(linkedTo) {
      if (!linkedTo || !this.workDict || !this.workDict[linkedTo]) return null;
      return this.getJobLabel(this.workDict[linkedTo]);
    },

    formatDate: function(dateStr) {
      if (!dateStr) return "";
      var date = new Date(dateStr);
      return date.toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric"
      });
    },

    toggleVerify: function(item) {
      if (item.status === "verified") {
        this.unverifyItem(item);
      } else {
        this.verifyItem(item);
      }
    },

    verifyItem: function(item) {
      var self = this;
      SmallWinsApi.UpdateSmallWin(item.id, { status: "verified" }, item).then(function(result) {
        if (result.success) {
          self.showSnackbar("Marked as verified!", "success");
          ProfileContextApi.ClearProfileContextCache();
          self.$store.dispatch("smallWins/loadSmallWins");
        } else {
          self.showSnackbar("Failed to update status", "error");
        }
      }).catch(function() {
        self.showSnackbar("Error updating status", "error");
      });
    },

    unverifyItem: function(item) {
      var self = this;
      SmallWinsApi.UpdateSmallWin(item.id, { status: "inferred" }, item).then(function(result) {
        if (result.success) {
          self.showSnackbar("Marked as needs review", "info");
          ProfileContextApi.ClearProfileContextCache();
          self.$store.dispatch("smallWins/loadSmallWins");
        } else {
          self.showSnackbar("Failed to update status", "error");
        }
      }).catch(function() {
        self.showSnackbar("Error updating status", "error");
      });
    },

    openEditDialog: function(item) {
      var draft = item.aiDraft || {};
      var month = item.eventMonth || null;
      var year = item.eventYear || null;
      if (!month && item.eventDate) {
        var d = new Date(item.eventDate);
        if (!isNaN(d.getTime())) {
          month = d.getMonth() + 1;
          year = d.getFullYear();
        }
      }
      if (!month && item.createdAt) {
        var c = new Date(item.createdAt);
        if (!isNaN(c.getTime())) {
          month = c.getMonth() + 1;
          year = c.getFullYear();
        }
      }
      this.editItem = {
        id: item.id,
        rawInput: item.rawInput || item.description || "",
        situation: draft.situation || "",
        task: draft.task || "",
        action: draft.action || "",
        result: draft.result || "",
        linkedTo: item.linkedTo || null,
        tags: (item.tags || []).slice(),
        eventMonth: month,
        eventYear: year,
      };
      this.newTag = "";
      this.editDialog = true;
      var self = this;
      this.$nextTick(function() {
        if (self.$refs.editForm) self.$refs.editForm.resetValidation();
      });
    },

    closeEditDialog: function() {
      this.editDialog = false;
      this.newTag = "";
    },

    saveEntry: function() {
      if (!this.editItem.id) return;
      var self = this;
      this.saving = true;

      var hasStar = this.editItem.situation || this.editItem.task || this.editItem.action || this.editItem.result;
      var updates = {
        rawInput: this.editItem.rawInput,
        description: this.editItem.rawInput,
        status: "verified",
        parsedType: hasStar ? "achievement" : "note",
        category: hasStar ? "achievement" : "note",
        aiDraft: {
          summary: this.editItem.rawInput,
          situation: this.editItem.situation,
          task: this.editItem.task,
          action: this.editItem.action,
          result: this.editItem.result
        },
        context: this.editItem.situation || "",
        task: this.editItem.task || "",
        action: this.editItem.action || "",
        outcome: this.editItem.result || "",
        linkedTo: this.editItem.linkedTo,
        tags: this.editItem.tags || [],
      };
      if (this.editItem.eventMonth) {
        updates.eventMonth = this.editItem.eventMonth;
      }
      if (this.editItem.eventYear) {
        updates.eventYear = this.editItem.eventYear;
      }
      if (this.editItem.eventMonth && this.editItem.eventYear) {
        updates.eventDate = new Date(this.editItem.eventYear, this.editItem.eventMonth - 1, 1).toISOString();
      }

      SmallWinsApi.UpdateSmallWin(this.editItem.id, updates, this.editItem).then(function(result) {
        if (result.success) {
          self.showSnackbar("Saved!", "success");
          self.editDialog = false;
          ProfileContextApi.ClearProfileContextCache();
          self.$store.dispatch("smallWins/loadSmallWins");
          self.loadAvailableTags();
        } else {
          self.showSnackbar("Failed to save", "error");
        }
      }).catch(function() {
        self.showSnackbar("Error saving entry", "error");
      }).finally(function() {
        self.saving = false;
      });
    },

    confirmDelete: function(item) {
      this.deleteItemId = item.id;
      this.deleteItemHint = item;
      // PR-WIN-GRADUATE-INTEGRITY: if this win is on the resume, surface a
      // clear warning that deleting it here does NOT remove the accomplishment
      // from the resume. Deleting only removes the win record.
      if (item && item.graduated) {
        var roleLabel = this.getLinkedJobLabel(item.linkedTo);
        this.deleteGraduatedWarning = roleLabel
          ? "This win is on your resume under " +
            roleLabel +
            ". Deleting it here will NOT remove it from your resume."
          : "This win is on your resume. Deleting it here will NOT remove it from your resume.";
      } else {
        this.deleteGraduatedWarning = "";
      }
      this.deleteDialog = true;
    },

    deleteEntry: function() {
      var self = this;
      this.saving = true;
      SmallWinsApi.DeleteSmallWin(this.deleteItemId, this.deleteItemHint).then(function(result) {
        if (result.success) {
          self.showSnackbar("Entry deleted", "info");
          self.deleteDialog = false;
          ProfileContextApi.ClearProfileContextCache();
          self.$store.dispatch("smallWins/loadSmallWins");
        } else {
          self.showSnackbar("Failed to delete entry", "error");
        }
      }).catch(function() {
        self.showSnackbar("Error deleting entry", "error");
      }).finally(function() {
        self.saving = false;
      });
    },

    expandToSage: function(item) {
      var text = (item.aiDraft && item.aiDraft.summary) || item.rawInput || "";
      // PR-GO-DEEPER-PARITY: pass existingWinId so Sage edits the SAME record
      // in place (no duplicate win) - mirrors SmallWinsCapture.expandWinToGuided.
      var query = { accomplishment: text, existingWinId: item.id };
      if (item.aiDraft) {
        if (item.aiDraft.situation) query.situation = item.aiDraft.situation;
        if (item.aiDraft.task) query.task = item.aiDraft.task;
        if (item.aiDraft.action) query.action = item.aiDraft.action;
        if (item.aiDraft.result) query.result = item.aiDraft.result;
      }
      if (item.rawInput && item.rawInput !== text) {
        query.rawInput = item.rawInput;
      }
      if (item.linkedTo) {
        query.linkedTo = item.linkedTo;
      }
      this.$router.push({ path: "/Sage", query: query });
    },

    toggleLinkedJob: function(jobKey) {
      this.editItem.linkedTo = this.editItem.linkedTo === jobKey ? null : jobKey;
    },

    isTagSelected: function(tag) {
      return (this.editItem.tags || []).indexOf(tag) >= 0;
    },

    toggleTag: function(tag) {
      var tags = (this.editItem.tags || []).slice();
      var idx = tags.indexOf(tag);
      if (idx >= 0) {
        tags.splice(idx, 1);
      } else {
        tags.push(tag);
      }
      this.editItem.tags = tags;
    },

    addTag: function() {
      var raw = (this.newTag || "").trim();
      if (!raw) return;
      var tags = (this.editItem.tags || []).slice();
      var exists = tags.some(function(t) { return t.toLowerCase() === raw.toLowerCase(); });
      if (!exists) {
        tags.push(raw);
        this.editItem.tags = tags;
      }
      this.newTag = "";
    },

    // PR-WIN-GRADUATE-CONFIRM: open the two-phase confirm dialog for a win from
    // the row kebab. Defaults the role pick to the win's current linkedTo so a
    // linked win is one click away; an unlinked win starts empty and the user
    // must choose a role (this fixes the old silent refusal).
    openGradDialog: function(win) {
      if (!win || win.graduated) return;
      this.gradWin = win;
      this.gradRole = win.linkedTo || null;
      this.gradPhase = "confirm";
      this.gradError = "";
      this.gradResultKey = null;
      this.gradResultLabel = "";
      // PR-WIN-GRADUATE-OPTIMIZE: seed the editable bullet from the win's
      // joined STAR (or raw input). The user can edit it manually or optimize
      // it with AI before confirming; the final value is saved to the
      // accomplishment.
      this.gradBulletText = this.buildWinBullet(win);
      this.gradDialog = true;
    },

    // PR-WIN-GRADUATE-OPTIMIZE: join a win's STAR fields into a single bullet,
    // falling back to the raw input. Mirrors the displayAuto that graduateWin
    // builds, so the seeded text matches what would otherwise be added.
    buildWinBullet: function(win) {
      if (!win) return "";
      var draft = win.aiDraft || {};
      var parts = [draft.situation, draft.task, draft.action, draft.result];
      var joined = parts
        .map(function(p) { return (p || "").trim(); })
        .filter(function(p) { return p; })
        .join(" ");
      if (joined) return joined;
      return (win.rawInput || win.description || "").trim();
    },

    // PR-WIN-GRADUATE-OPTIMIZE: <ai-optimize-phrasing> emits the accepted text
    // here. Replace the editable bullet with the optimized version; it will be
    // saved to the accomplishment on confirm.
    onGradPhrasingAccepted: function(newPhrasing) {
      this.gradBulletText = newPhrasing;
    },

    // PR-WIN-GRADUATE-CONFIRM: open the confirm dialog from inside the edit
    // dialog. Builds a win from the CURRENT edit fields so any unsaved STAR
    // detail the user just typed is what lands on the resume, and carries the
    // persisted graduated flag from the store win so an already-promoted win is
    // blocked.
    openGradDialogFromEdit: function() {
      if (!this.editItem || !this.editItem.id) return;
      var storeWin = this.editItemWin || {};
      var win = {
        id: this.editItem.id,
        linkedTo: this.editItem.linkedTo,
        graduated: storeWin.graduated === true,
        rawInput: this.editItem.rawInput,
        description: this.editItem.rawInput,
        aiDraft: {
          situation: this.editItem.situation,
          task: this.editItem.task,
          action: this.editItem.action,
          result: this.editItem.result
        }
      };
      this.openGradDialog(win);
    },

    closeGradDialog: function() {
      this.gradDialog = false;
      this.gradWin = null;
      this.gradRole = null;
      this.gradError = "";
      this.gradPhase = "confirm";
      this.gradBulletText = "";
    },

    // PR-WIN-GRADUATE-CONFIRM: route into the Work Accomplishments editor for
    // the role the win landed under, so "View in Experience" drops the user
    // exactly where the new accomplishment lives.
    viewInExperience: function() {
      var key = this.gradResultKey;
      this.closeGradDialog();
      if (key) {
        this.$router
          .push("/WorkAccomplishments/" + key)
          .catch(function() {});
      }
    },

    // PR-WIN-GRADUATE-CONFIRM: confirm handler. Promotes the dialog's win under
    // the SELECTED role (not necessarily the win's old linkedTo). On success it
    // flips the same dialog to the success phase; on failure it shows the error
    // inline and leaves the win un-graduated so the user can retry.
    confirmGraduate: function() {
      var self = this;
      var win = this.gradWin;
      var roleKey = this.gradRole;
      if (!win || !roleKey) return;
      this.gradError = "";
      this.graduateWin(win, roleKey)
        .then(function(result) {
          if (result && result.ok) {
            self.gradResultKey = result.roleKey;
            self.gradResultLabel = result.roleLabel;
            self.gradPhase = "success";
          } else {
            self.gradError =
              (result && result.message) ||
              "Could not add this win to your resume. Please try again.";
          }
        });
    },

    // PR-WIN-GRADUATE: does this win carry any STAR content worth promoting?
    // A win can be promoted once it has at least one STAR field OR raw input -
    // an empty capture has nothing to copy into an accomplishment.
    winHasStarContent: function(win) {
      if (!win) return false;
      var draft = win.aiDraft || {};
      return !!(
        (draft.situation && String(draft.situation).trim()) ||
        (draft.task && String(draft.task).trim()) ||
        (draft.action && String(draft.action).trim()) ||
        (draft.result && String(draft.result).trim()) ||
        (win.rawInput && String(win.rawInput).trim()) ||
        (win.description && String(win.description).trim())
      );
    },

    // PR-WIN-GRADUATE-CONFIRM: promote a Small Win into a resume-grade Work
    // Accomplishment under the SELECTED role (targetRoleKey - chosen in the
    // confirm dialog, which may differ from the win's old linkedTo). The win is
    // left in place and tagged graduated so it cannot be promoted again and its
    // evidence is not double-counted on the profile. Maps the win STAR draft
    // onto the work achievement BRAM fields, adds it via the same work-store
    // path the accomplishments page uses (setAchievement + saveWork), then
    // marks the win (and re-points its linkedTo when the role moved). Returns a
    // Promise resolving to { ok, roleKey, roleLabel, message } so the dialog
    // can flip to its success state or show an inline error for retry.
    graduateWin: function(win, targetRoleKey) {
      var self = this;
      if (!win || this.graduating) {
        return Promise.resolve({ ok: false, message: "Please wait..." });
      }
      if (win.graduated) {
        // Already on the resume - guarded by the UI, belt-and-suspenders here.
        return Promise.resolve({
          ok: false,
          message: "This win is already on your resume.",
        });
      }
      // The user picks the role in the confirm dialog; fall back to the win's
      // linkedTo if a caller did not pass one.
      var roleKey = targetRoleKey || win.linkedTo;
      if (!roleKey) {
        return Promise.resolve({
          ok: false,
          message: "Choose a role to add this win under.",
        });
      }
      // The chosen role must still exist in the work dictionary.
      var role = this.workDict && this.workDict[roleKey];
      if (!role) {
        var noRoleMsg =
          "That role no longer exists. Pick another role.";
        this.showSnackbar(noRoleMsg, "error");
        return Promise.resolve({ ok: false, message: noRoleMsg });
      }
      // Precondition: must have some STAR content.
      if (!this.winHasStarContent(win)) {
        var noStarMsg =
          "Add some detail to this win first, then add it to your resume.";
        this.showSnackbar(noStarMsg, "info");
        return Promise.resolve({ ok: false, message: noStarMsg });
      }

      var draft = win.aiDraft || {};
      var background = (draft.situation || "").trim();
      var responsibility = (draft.task || "").trim();
      var actionResult = (draft.action || "").trim();
      var measurement = (draft.result || "").trim();
      // If the win has no STAR breakdown at all, fall back to the raw input as
      // the action/result line so the accomplishment is not empty.
      if (!background && !responsibility && !actionResult && !measurement) {
        actionResult = (win.rawInput || win.description || "").trim();
      }
      // Mirror the accomplishments page / setAchievement: displayAuto is the
      // joined BRAM text.
      var displayAuto = (
        background +
        " " +
        responsibility +
        " " +
        actionResult +
        " " +
        measurement
      ).trim();
      // PR-WIN-GRADUATE-OPTIMIZE: the user may have edited or AI-optimized the
      // bullet in the confirm dialog. Save that refined text as displayCustom
      // with useCustom: true so it is what shows on the resume, while the
      // underlying STAR/BRAM fields are still stored. Fall back to displayAuto
      // when the editable bullet is empty.
      var finalBullet = (this.gradBulletText || "").trim() || displayAuto;
      var achievementId = uuid.gen();
      var achievement = {
        background: background,
        responsibility: responsibility,
        actionResult: actionResult,
        measurement: measurement,
        displayAuto: displayAuto,
        displayCustom: finalBullet,
        useCustom: true,
        // PR-WIN-GRADUATE-INTEGRITY: back-reference so deleting this
        // accomplishment in Work Experience can un-flag the source win.
        sourceWinId: win.id,
        id: achievementId
      };

      var roleLabel = this.getJobLabel(role);
      this.graduating = true;

      // Add the achievement to the linked role and persist Work. setAchievement
      // operates on the active work key, so point it at the linked role first,
      // then restore the previous key to avoid side-effecting other views.
      var prevKey = this.$store.state.work.key;
      this.$store.commit("work/updateField", { path: "key", value: roleKey });
      this.$store.commit("work/setAchievement", achievement);
      this.$store.commit("work/updateField", { path: "key", value: prevKey });

      this.$store
        .dispatch("work/saveWork")
        .then(function(saved) {
          if (!saved) {
            // Roll back the in-memory achievement so a retry does not stack a
            // duplicate, and leave the win un-graduated.
            self.$store.commit("work/updateField", {
              path: "key",
              value: roleKey
            });
            self.$store.commit("work/removeAchievement", achievementId);
            self.$store.commit("work/updateField", {
              path: "key",
              value: prevKey
            });
            self.showSnackbar(
              "Could not add this win to your resume. Please try again.",
              "error"
            );
            return null;
          }
          // Work persisted - now tag the win as graduated.
          return SmallWinsApi.UpdateSmallWin(
            win.id,
            {
              graduated: true,
              graduatedAchievementId: achievementId,
              graduatedAt: new Date().toISOString()
            },
            win
          ).then(function(result) {
            if (result && result.success) {
              self.showSnackbar(
                "Added to your resume under " + roleLabel + ".",
                "success"
              );
              if (self.editDialog && self.editItem.id === win.id) {
                self.closeEditDialog();
              }
              ProfileContextApi.ClearProfileContextCache();
              self.$store.dispatch("smallWins/loadSmallWins");
            } else {
              // Work saved but the win tag did not. The achievement is on the
              // resume; warn so the user knows not to promote again (it would
              // duplicate) - the win simply stays untagged until next save.
              self.showSnackbar(
                "Added to your resume, but could not tag this win. Avoid adding it again.",
                "warning"
              );
            }
            return result;
          });
        })
        .catch(function() {
          self.showSnackbar(
            "Could not add this win to your resume. Please try again.",
            "error"
          );
        })
        .finally(function() {
          self.graduating = false;
        });
    },

    showSnackbar: function(text, color) {
      this.snackbarText = text;
      this.snackbarColor = color || "success";
      this.snackbar = true;
    },
  },
};
</script>

<style lang="css" scoped>
.headline-text {
  font-size: 24px;
  font-weight: 500;
}

/* RESPONSIVE-HEADER-FIX: let the title/description block flex to fill the
   available width (no fixed narrow column) and keep the action buttons from
   being squeezed. The button group never shrinks below its content so labels
   stay readable; it wraps to a new line instead of overflowing. */
.header-text-block {
  flex: 1 1 auto;
  min-width: 0;
}

.header-actions {
  flex: 0 0 auto;
}

.dialog-title {
  font-size: 20px;
  font-weight: 500;
}

.simpleCard {
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.promote-preview-box {
  background: #f5f5f5;
  border-radius: 8px;
}

/* PR-WIN-GRADUATE: keep the "On resume" marker quiet - it is a status note,
   not a call to action. */
.on-resume-chip {
  font-weight: 500;
  letter-spacing: 0.2px;
}
</style>

<style>
.theme--dark .simpleCard {
  border-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .promote-preview-box {
  background: #2D2D2D;
}

.description-cell {
  max-width: 350px;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

/* MOBILE-TABLE-FIX: in the stacked mobile layout (<960px) Vuetify renders
   each row as label/value pairs. Let the summary use the full row width so
   the action + summary stay readable instead of being clamped to 350px in a
   narrow column, and give the stacked rows breathing room. */
@media (max-width: 959px) {
  .small-wins-table >>> .v-data-table__mobile-row {
    min-height: 40px;
  }
  .small-wins-table >>> .v-data-table__mobile-row__cell {
    max-width: 65%;
    text-align: right;
  }
  .small-wins-table >>> .description-cell {
    max-width: 100%;
    text-align: right;
  }
}
</style>
